import {
  classifySchoolBlockCategory,
  classifySchoolSentenceCategory,
  hasStandaloneFoodEventSignal,
  hasEventShiftCue,
  isBehaviorContinuationSentence,
  isCategoryContinuationSentence,
  isSchoolIntroOrClosing,
  looksLikeSchoolEvent,
  parseSchoolEventImportance,
} from './importLogsParsingClassification';
import {
  buildDateWithOptionalTime,
  extractTimeFromText,
} from './importLogsParsingTime';
import { splitIntoSentences } from './importLogsParsingText';

const stripBulletPrefix = (line = '') => String(line || '').replace(/^\s*(?:[-*•]|\d+[.)])\s+/u, '').trim();

const getChunkText = (chunk = null) => {
  if (!chunk || typeof chunk !== 'object') {
    return '';
  }

  return Array.isArray(chunk.sentences)
    ? chunk.sentences.join(' ').trim()
    : String(chunk.text || '').trim();
};

const finalizeChunk = (chunk = null) => {
  const text = getChunkText(chunk);
  if (!text) {
    return null;
  }

  return {
    text,
    time: chunk?.time || null,
    category: classifySchoolBlockCategory(text) || chunk?.category || null,
  };
};

const createChunkState = ({ sentence = '', time = null, category = null } = {}) => ({
  sentences: sentence ? [sentence] : [],
  time: time || null,
  category: category || null,
});

const appendSentenceToChunk = (chunk = null, sentence = '', time = null, category = null) => {
  const safeChunk = chunk && typeof chunk === 'object' ? chunk : {};
  const sentences = Array.isArray(safeChunk.sentences) ? safeChunk.sentences.concat(sentence) : [sentence];
  return {
    sentences,
    time: safeChunk.time || time || null,
    category: classifySchoolBlockCategory(sentences.join(' ')) || safeChunk.category || category || null,
  };
};

const shouldSplitChunk = ({
  currentChunk = {},
  sentence = '',
  sentenceTime = {},
  sentenceCategory = null,
  sentenceHasStrongSignal = false,
} = {}) => {
  const currentText = getChunkText(currentChunk);
  const currentCategory = classifySchoolBlockCategory(currentText) || currentChunk.category || null;
  const nextCombinedText = [currentText, sentence].filter(Boolean).join(' ').trim();
  const combinedCategory = classifySchoolBlockCategory(nextCombinedText);
  const hasExplicitTimeBoundary = sentenceTime.time && currentChunk.time && sentenceTime.time !== currentChunk.time;
  const sentenceContinuesCurrent = currentCategory && isCategoryContinuationSentence(currentCategory, sentence);
  const sameCategory = Boolean(
    currentCategory
    && sentenceCategory
    && sentenceCategory === currentCategory
  );
  const sentenceHasShiftCue = hasEventShiftCue(sentence);

  if (hasExplicitTimeBoundary) {
    if (sentenceContinuesCurrent && sameCategory) {
      return false;
    }

    if (!sentenceHasShiftCue && combinedCategory && combinedCategory === currentCategory) {
      return false;
    }

    return true;
  }

  if (currentCategory === 'behavior' && sentenceCategory === 'mood' && isBehaviorContinuationSentence(sentence)) {
    return false;
  }

  if (currentCategory === 'behavior' && sentenceHasShiftCue && hasStandaloneFoodEventSignal(sentence)) {
    return true;
  }

  if (sentenceContinuesCurrent) {
    return false;
  }

  if (sentenceHasShiftCue && sentenceHasStrongSignal && currentCategory) {
    return true;
  }

  if (!sentenceHasStrongSignal) {
    return !currentCategory;
  }

  if (!currentCategory) {
    return false;
  }

  if (combinedCategory && combinedCategory === currentCategory) {
    return false;
  }

  if (sentenceCategory && sentenceCategory !== currentCategory) {
    return true;
  }

  return false;
};

const buildSchoolEventParagraphs = (rawText = '') => String(rawText || '')
  .replace(/\r\n/g, '\n')
  .split(/\n{2,}/)
  .map((block) => block.split('\n').map((line) => String(line || '').trim()).filter(Boolean).join(' '))
  .map((block) => block.replace(/\s+/g, ' ').trim())
  .filter(Boolean);

export const buildSchoolEventChunks = (rawText = '') => {
  const paragraphs = buildSchoolEventParagraphs(rawText);

  const chunks = [];

  paragraphs.forEach((paragraph) => {
    const sentences = splitIntoSentences(paragraph);
    if (!sentences.length) return;

    let currentChunk = null;
    const flushChunk = () => {
      const finalizedChunk = finalizeChunk(currentChunk);
      if (finalizedChunk?.text) chunks.push(finalizedChunk);
      currentChunk = null;
    };

    sentences.forEach((sentence) => {
      const trimmedSentence = stripBulletPrefix(sentence).trim();
      if (!trimmedSentence || isSchoolIntroOrClosing(trimmedSentence)) return;

      const sentenceTime = extractTimeFromText(trimmedSentence);
      const sentenceCategory = classifySchoolSentenceCategory(trimmedSentence);
      const sentenceHasStrongSignal = Boolean(sentenceCategory || looksLikeSchoolEvent(trimmedSentence));

      if (!currentChunk) {
        if (!sentenceHasStrongSignal) return;
        currentChunk = createChunkState({
          sentence: trimmedSentence,
          time: sentenceTime.time,
          category: sentenceCategory,
        });
        return;
      }

      if (shouldSplitChunk({
        currentChunk,
        sentence: trimmedSentence,
        sentenceTime,
        sentenceCategory,
        sentenceHasStrongSignal,
      })) {
        flushChunk();
        if (sentenceHasStrongSignal) {
          currentChunk = createChunkState({
            sentence: trimmedSentence,
            time: sentenceTime.time,
            category: sentenceCategory,
          });
        }
        return;
      }

      currentChunk = appendSentenceToChunk(currentChunk, trimmedSentence, sentenceTime.time, sentenceCategory);
    });

    flushChunk();
  });

  return chunks;
};

const mapQuickNoteCategoryToImportCategory = (category) => {
  switch (category) {
    case 'behavior':
    case 'health':
    case 'medication':
    case 'milestone':
    case 'mood':
    case 'sleep':
    case 'food':
    case 'activity':
      return category;
    default:
      return 'log';
  }
};

export const resolveInheritedChunkTime = ({
  resolvedTime = null,
  extractedTime = null,
  approximateTime = null,
  category = '',
  cleanText = '',
  state = {},
} = {}) => {
  const canInheritTime = Boolean(
    !resolvedTime
    && !extractedTime
    && !approximateTime
    && state.lastResolvedTime
    && category
    && category === state.lastCategory
    && isCategoryContinuationSentence(category, cleanText)
  );

  return {
    canInheritTime,
    finalTime: resolvedTime || extractedTime || approximateTime || (canInheritTime ? state.lastResolvedTime : null),
    timeSource: resolvedTime || extractedTime
      ? 'explicit'
      : approximateTime
        ? 'approximate'
        : canInheritTime
          ? 'inferred'
          : 'missing',
  };
};

export const shouldMergeWithPreviousImportedRow = ({
  state = {},
  category = '',
  resolvedTime = null,
  extractedTime = null,
  approximateTime = null,
  cleanText = '',
} = {}) => {
  const prevRow = state.rows.length ? state.rows[state.rows.length - 1] : null;
  if (!prevRow) {
    return null;
  }

  const isTimelessContinuation = Boolean(
    category === (prevRow.parsedSourceCategory || prevRow.category)
    && !resolvedTime && !extractedTime && !approximateTime
    && isCategoryContinuationSentence(category, cleanText)
  );

  const isTimedSameEpisodeContinuation = Boolean(
    category === (prevRow.parsedSourceCategory || prevRow.category)
    && (resolvedTime || extractedTime || approximateTime)
    && /^(?:not long after|shortly after|soon after|a little later)\b/i.test(String(cleanText || '').trim())
    && (
      isCategoryContinuationSentence(category, cleanText)
      || classifySchoolBlockCategory(`${prevRow.note} ${cleanText}`) === category
    )
  );

  return (isTimelessContinuation || isTimedSameEpisodeContinuation) ? prevRow : null;
};

export const buildImportRowFromChunk = ({
  index = 0,
  defaultDate = new Date(),
  childName = null,
  category = '',
  cleanText = '',
  finalTime = null,
  timeSource = 'missing',
  categorySource = 'fallback',
  confidenceState = {},
  parsedObservation = false,
} = {}) => ({
  id: `${Date.now()}-${index}`,
  date: buildDateWithOptionalTime(defaultDate, finalTime, cleanText) || null,
  note: cleanText,
  body: cleanText,
  category: mapQuickNoteCategoryToImportCategory(category),
  importance: parseSchoolEventImportance(cleanText),
  childName,
  include: true,
  parsedObservation,
  parsedSourceCategory: category,
  parsedSourceType: category,
  parsedSourceConfidence: confidenceState.confidence,
  reviewConfidence: confidenceState.confidence,
  reviewConfidenceLabel: confidenceState.confidenceLabel,
  reviewConfidenceReasons: confidenceState.confidenceReasons,
  timeSource,
  localTime: finalTime || null,
  categorySource,
});
